import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { ADS_COST_CODES, ADS_CREDIT_CODES, invoiceTypeLabel } from '../../lib/emagInvoiceTypes'
import { Card } from '../../components/ui/Card'
import { Spinner } from '../../components/ui/Spinner'
import { DateRangeField, currentYearRange } from '../../components/ui/DateField'
import { CostsCard } from './CostsCard'

interface DashboardData {
  orders: {
    count: number
    value: number
    recurring: number
    recurringClients: number
    recurringPct: number
    cardCount: number
    cardValue: number
    courierCount: number
    courierValue: number
    geniusCount: number
    geniusClients: number
    geniusValue: number
    geniusPct: number
    lockerCount: number
    lockerValue: number
    lockerPct: number
    returnCount: number
    returnValue: number
    valueCount: number
    returnRate: number
    byStatus: { status: number; label: string; count: number; value: number }[]
  }
  sell: {
    count: number
    value: number
    grossValue: number
    stornoCount: number
    stornoValue: number
  }
  commissions: {
    count: number
    value: number
    byCategory: { category: string; categoryName: string; count: number; value: number }[]
  }
  campaign: {
    voucherCount: number
    voucherNet: number
    voucherGross: number
    voucherSellerNet: number
    voucherSellerGross: number
    voucherEmagNet: number
    voucherEmagGross: number
    pricecutCount: number
    pricecutNet: number
    pricecutGross: number
    totalGross: number
    totalNet: number
  }
  campaignTrend: {
    month: string
    voucherSellerGross: number
    voucherEmagGross: number
    pricecutGross: number
    totalGross: number
  }[]
  trend: {
    month: string
    count: number
    value: number
    recurringPct: number
    cardPct: number
    courierPct: number
    returnPct: number
  }[]
  topProducts: { name: string; qty: number; value: number }[]
}

const lei = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const nr = (n: number) => n.toLocaleString('ro-RO')

type Tone = 'blue' | 'amber' | 'green' | 'muted'
const TONE_BG: Record<Tone, string> = {
  blue: 'border-l-4 border-l-b4l-blue bg-b4l-blue-light/20',
  amber: 'border-l-4 border-l-amber-500 bg-amber-50',
  green: 'border-l-4 border-l-b4l-green-dark bg-b4l-green-light/20',
  muted: 'border-l-4 border-l-b4l-text-muted/50 bg-b4l-bg-soft/50',
}
const TONE_VAL: Record<Tone, string> = {
  blue: 'text-b4l-blue-dark',
  amber: 'text-amber-700',
  green: 'text-b4l-green-dark',
  muted: 'text-b4l-text-muted',
}

function Tile({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: string
  sub?: string
  tone?: Tone
}) {
  return (
    <div
      className={`rounded-lg border border-b4l-border bg-white p-3 ${tone ? TONE_BG[tone] : ''}`}
    >
      <div className="truncate text-xs text-b4l-text-muted">{label}</div>
      <div
        className={`text-lg font-semibold leading-tight sm:text-xl ${tone ? TONE_VAL[tone] : ''}`}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-b4l-text-muted">{sub}</div>}
    </div>
  )
}

function fmtLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

type PctPoint = {
  month: string
  recurringPct: number
  cardPct: number
  courierPct: number
  returnPct: number
}

function PctTrendChart({ data }: { data: PctPoint[] }) {
  if (data.length === 0) return null
  const W = 100
  const H = 42
  const n = data.length
  const px = (i: number) => (n === 1 ? W / 2 : (i / (n - 1)) * W)
  const py = (p: number) => H - (Math.max(0, Math.min(100, p)) / 100) * H
  const series: { key: keyof Omit<PctPoint, 'month'>; color: string; label: string }[] = [
    { key: 'recurringPct', color: '#16a34a', label: 'Recurenți' },
    { key: 'cardPct', color: '#378ADD', label: 'Card' },
    { key: 'courierPct', color: '#d97706', label: 'Ramburs' },
    { key: 'returnPct', color: '#dc2626', label: 'Retururi' },
  ]
  return (
    <div>
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-36 w-full">
          {[0, 25, 50, 75, 100].map((g) => (
            <line key={g} x1={0} x2={W} y1={py(g)} y2={py(g)} stroke="#e5e7eb" strokeWidth={0.3} />
          ))}
          {series.map((s) => (
            <polyline
              key={s.key}
              points={data.map((d, i) => `${px(i)},${py(d[s.key])}`).join(' ')}
              fill="none"
              stroke={s.color}
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>
        <span className="absolute left-0 top-0 text-[9px] text-b4l-text-muted">100%</span>
        <span className="absolute bottom-0 left-0 text-[9px] text-b4l-text-muted">0%</span>
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-b4l-text-muted">
        {data.map((d) => (
          <span key={d.month}>{d.month.slice(2)}</span>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px]">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: s.color }}
            />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  )
}

export function DashboardEmagPage() {
  const [dateFrom, setDateFrom] = useState(currentYearRange().from)
  const [dateTo, setDateTo] = useState(currentYearRange().to)

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['emag-dashboard', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (dateFrom) params.set('dateFrom', dateFrom)
      if (dateTo) params.set('dateTo', dateTo)
      return (await api.get(`/emag/dashboard?${params}`)).data
    },
  })

  const trendMax = data ? Math.max(1, ...data.trend.map((t) => t.value)) : 1

  const exportCampaign = () => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('dateFrom', dateFrom)
    if (dateTo) params.set('dateTo', dateTo)
    void downloadXlsx(`/emag/campaign/export?${params}`, 'suport-campanie-emag.xlsx')
  }

  const setRange = (from: Date, to: Date) => {
    setDateFrom(fmtLocal(from))
    setDateTo(fmtLocal(to))
  }
  const now = new Date()
  const quick = {
    lunaCurenta: () =>
      setRange(
        new Date(now.getFullYear(), now.getMonth(), 1),
        new Date(now.getFullYear(), now.getMonth() + 1, 0),
      ),
    lunaPrecedenta: () =>
      setRange(
        new Date(now.getFullYear(), now.getMonth() - 1, 1),
        new Date(now.getFullYear(), now.getMonth(), 0),
      ),
    anCurent: () =>
      setRange(new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31)),
    anPrecedent: () =>
      setRange(new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31)),
    toate: () => {
      setDateFrom('')
      setDateTo('')
    },
  }
  const rangeIs = (from: Date, to: Date) => dateFrom === fmtLocal(from) && dateTo === fmtLocal(to)
  const activePreset =
    !dateFrom && !dateTo
      ? 'toate'
      : rangeIs(
            new Date(now.getFullYear(), now.getMonth(), 1),
            new Date(now.getFullYear(), now.getMonth() + 1, 0),
          )
        ? 'lunaCurenta'
        : rangeIs(
              new Date(now.getFullYear(), now.getMonth() - 1, 1),
              new Date(now.getFullYear(), now.getMonth(), 0),
            )
          ? 'lunaPrecedenta'
          : rangeIs(new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31))
            ? 'anCurent'
            : rangeIs(
                  new Date(now.getFullYear() - 1, 0, 1),
                  new Date(now.getFullYear() - 1, 11, 31),
                )
              ? 'anPrecedent'
              : ''
  const quickBtns = [
    { key: 'lunaCurenta', label: 'Luna curentă', fn: quick.lunaCurenta },
    { key: 'lunaPrecedenta', label: 'Luna precedentă', fn: quick.lunaPrecedenta },
    { key: 'anCurent', label: 'Anul curent', fn: quick.anCurent },
    { key: 'anPrecedent', label: 'An precedent', fn: quick.anPrecedent },
    { key: 'toate', label: 'Toate', fn: quick.toate },
  ]

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <h1 className="text-lg font-semibold sm:text-xl">eMAG → Tablou de bord</h1>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-end">
          <div className="flex flex-wrap gap-2">
            {quickBtns.map((b) => (
              <button
                key={b.key}
                onClick={b.fn}
                className={`rounded-md border px-3.5 py-2 text-sm font-semibold shadow-sm transition ${
                  activePreset === b.key
                    ? 'border-b4l-blue bg-b4l-blue text-white'
                    : 'border-b4l-border bg-white text-b4l-text hover:border-b4l-blue hover:bg-b4l-blue-light/30 hover:text-b4l-blue-dark'
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
          <div className="w-full sm:w-72">
            <DateRangeField
              fromValue={dateFrom}
              toValue={dateTo}
              onChange={(f, t) => {
                setDateFrom(f)
                setDateTo(t)
              }}
            />
          </div>
        </div>
      </div>

      {isLoading && (
        <Card>
          <Spinner label="Se încarcă datele eMAG…" />
        </Card>
      )}

      {data && (
        <>
          {/* KPI-uri */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Tile label="Nr. comenzi" value={nr(data.orders.count)} sub="fără anulate" />
            <Tile
              label="Valoare comenzi"
              value={`${lei(data.orders.value)} lei`}
              sub="cu TVA · fără anulate și returnate"
            />
            <Tile
              label="Recurenți"
              value={`${nr(data.orders.recurringClients)} clienți`}
              sub={`${data.orders.recurringPct}% din comenzi · ${nr(data.orders.recurring)} comenzi`}
            />
            <Tile
              label="Medie / comandă"
              value={`${lei(data.orders.valueCount > 0 ? data.orders.value / data.orders.valueCount : 0)} lei`}
              sub="pe comenzile nereturnate"
            />
            <Tile
              label="Plată card"
              value={`${lei(data.orders.cardValue)} lei`}
              sub={`${nr(data.orders.cardCount)} comenzi`}
            />
            <Tile
              label="Ramburs (curier)"
              value={`${lei(data.orders.courierValue)} lei`}
              sub={`${nr(data.orders.courierCount)} comenzi`}
            />
            <Tile
              label="Genius"
              value={`${nr(data.orders.geniusClients)} clienți`}
              sub={`${data.orders.geniusPct}% · ${nr(data.orders.geniusCount)} com. · ${lei(data.orders.geniusValue)} lei`}
            />
            <Tile
              label="Rata retur"
              value={`${data.orders.returnRate}%`}
              sub={`${nr(data.orders.returnCount)} retururi · ${lei(data.orders.returnValue)} lei`}
            />
            <Tile
              label="Livrare locker"
              value={`${data.orders.lockerPct}%`}
              sub={`${nr(data.orders.lockerCount)} com. · ${lei(data.orders.lockerValue)} lei`}
            />
            <Tile
              label="Facturi vânzare"
              value={`${lei(data.sell.value)} lei`}
              sub={`cu TVA · net de storno · ${nr(data.sell.count)} fact. · ${nr(data.sell.stornoCount)} storno (${lei(data.sell.stornoValue)} lei)`}
            />
          </div>

          {/* Costuri eMAG pe categorii (comision efectiv separat + celelalte costuri) +
              drill-down cu facturile Dante aferente. Acelasi component ca pe Incasari. */}
          <CostsCard from={dateFrom} to={dateTo} salesValue={data.sell.value} />

          {/* Cheltuieli Ads (promovare) — categorii FAP* */}
          {(() => {
            // Cost real de ads = FAPO (post-paid) + FACCP (clickuri pre-paid). Creditele
            // gratuite/avansurile (FAPOF/FAPC, FAACP/FAECP/FASCPA) se anuleaza — nu sunt cost.
            const ads = data.commissions.byCategory.filter((c) => ADS_COST_CODES.has(c.category))
            const adsValue = ads.reduce((a, c) => a + c.value, 0)
            const adsCount = ads.reduce((a, c) => a + c.count, 0)
            const freeCredit = data.commissions.byCategory.filter((c) =>
              ADS_CREDIT_CODES.has(c.category),
            )
            const freeCreditGross = freeCredit
              .filter((c) => c.value > 0)
              .reduce((a, c) => a + c.value, 0)
            const freeCreditNet = freeCredit.reduce((a, c) => a + c.value, 0)
            const acos = data.sell.value > 0 ? (adsValue / data.sell.value) * 100 : 0
            const adsPerOrder = data.orders.count > 0 ? adsValue / data.orders.count : 0
            // Comision real (doar FC/FCS), nu totalul facturilor Dante.
            const commOnly = data.commissions.byCategory
              .filter((c) => c.category === 'FC' || c.category === 'FCS')
              .reduce((a, c) => a + c.value, 0)
            const adsPctComm = commOnly > 0 ? (adsValue / commOnly) * 100 : 0
            return (
              <Card title="Cheltuieli Ads (promovare) — în corelație cu vânzările">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                  <Tile
                    label="Cheltuieli ads"
                    value={`${lei(adsValue)} lei`}
                    sub={`${nr(adsCount)} facturi`}
                  />
                  <Tile
                    label="Vânzări (perioadă)"
                    value={`${lei(data.sell.value)} lei`}
                    sub="bază pentru ACOS"
                  />
                  <Tile
                    label="ACOS (ads / vânzări)"
                    value={`${acos.toFixed(1)}%`}
                    sub="cât din vânzări merge pe ads"
                  />
                  <Tile label="Ads / comandă" value={`${lei(adsPerOrder)} lei`} />
                  <Tile label="% din comisioane" value={`${adsPctComm.toFixed(1)}%`} />
                </div>
                <p className="mt-2 text-[11px] text-b4l-text-muted">
                  Cheltuieli ads = FAPO (post-paid turnover), factură forfetară de servicii de
                  promovare conform Anexei — eMAG nu defalcă cifra de afaceri atribuită fiecărui
                  produs, deci corelația se face la nivel de portofoliu (ACOS = ads ÷ vânzări).
                  {freeCredit.length > 0 && (
                    <>
                      {' '}
                      FAPOF/FAPC = credit gratuit ads de {lei(freeCreditGross)} lei (facturat și
                      anulat integral prin discount, net {lei(freeCreditNet)} lei) — avans neutru,
                      neinclus în cheltuieli.
                    </>
                  )}
                </p>
              </Card>
            )
          })()}

          {/* Suport campanie — reduceri acordate clientului (vouchere + reducere pret listare) */}
          {(() => {
            const c = data.campaign
            const pctSales = data.sell.value > 0 ? (c.totalGross / data.sell.value) * 100 : 0
            const splitTotal = c.voucherSellerGross + c.voucherEmagGross
            const splitDiff = splitTotal - c.voucherGross
            const realCost = c.pricecutGross + c.voucherSellerGross
            return (
              <Card
                title="Suport campanie (reduceri acordate) — în corelație cu vânzările"
                actions={
                  <button
                    onClick={exportCampaign}
                    className="rounded-md border border-b4l-border bg-white px-3 py-1.5 text-xs font-semibold text-b4l-text shadow-sm transition hover:border-b4l-blue hover:text-b4l-blue-dark"
                  >
                    Export Excel
                  </button>
                }
              >
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Tile
                    label="Vouchere (cod reducere)"
                    value={`${lei(c.voucherGross)} lei`}
                    sub={`${nr(c.voucherCount)} vouchere · ${lei(c.voucherNet)} net`}
                    tone="blue"
                  />
                  <Tile
                    label="Reducere preț (listă vs vânzare)"
                    value={`${lei(c.pricecutGross)} lei`}
                    sub={`${nr(c.pricecutCount)} linii · ${lei(c.pricecutNet)} net`}
                    tone="amber"
                  />
                  <Tile
                    label="Total suport campanie"
                    value={`${lei(c.totalGross)} lei`}
                    sub={`${lei(c.totalNet)} net · vouchere + reducere preț`}
                  />
                  <Tile
                    label="% din vânzări"
                    value={`${pctSales.toFixed(1)}%`}
                    sub="cât reprezintă suportul din vânzări"
                  />
                </div>

                {/* Defalcare vouchere dupa cine suporta (offered_by) */}
                <div className="mt-3 rounded-lg border border-b4l-border bg-b4l-bg-soft/40 p-3">
                  <div className="mb-2 text-xs font-medium text-b4l-text-muted">
                    Vouchere — cine suportă reducerea (din <code>offered_by</code>)
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    <Tile
                      label="Vouchere suportate de TINE (seller)"
                      value={`${lei(c.voucherSellerGross)} lei`}
                      sub={`${lei(c.voucherSellerNet)} net · costul tău real`}
                      tone="blue"
                    />
                    <Tile
                      label="Vouchere suportate de eMAG"
                      value={`${lei(c.voucherEmagGross)} lei`}
                      sub={`${lei(c.voucherEmagNet)} net · nu te costă pe tine`}
                      tone="muted"
                    />
                    <Tile
                      label="Suport real vânzător"
                      value={`${lei(realCost)} lei`}
                      sub="reducere preț + vouchere seller"
                      tone="green"
                    />
                  </div>

                  {/* Reconciliere: seller + eMAG = total vouchere */}
                  <div className="mt-2 space-y-0.5 rounded border border-b4l-border bg-white px-2.5 py-2 text-[11px]">
                    <div className="flex flex-wrap items-center gap-x-1.5">
                      <span className="font-medium text-b4l-blue-dark">
                        {lei(c.voucherSellerGross)}
                      </span>
                      <span className="text-b4l-text-muted">(tu)</span>
                      <span>+</span>
                      <span className="font-medium text-b4l-text-muted">
                        {lei(c.voucherEmagGross)}
                      </span>
                      <span className="text-b4l-text-muted">(eMAG)</span>
                      <span>=</span>
                      <span className="font-semibold">{lei(splitTotal)}</span>
                      <span className="text-b4l-text-muted">
                        defalcare · headline {lei(c.voucherGross)} · diferență{' '}
                        {lei(Math.abs(splitDiff))} lei (vouchere pe transport)
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-1.5">
                      <span className="font-medium text-amber-700">{lei(c.pricecutGross)}</span>
                      <span className="text-b4l-text-muted">(reducere preț)</span>
                      <span>+</span>
                      <span className="font-medium text-b4l-blue-dark">
                        {lei(c.voucherSellerGross)}
                      </span>
                      <span className="text-b4l-text-muted">(vouchere seller)</span>
                      <span>=</span>
                      <span className="font-semibold text-b4l-green-dark">{lei(realCost)}</span>
                      <span className="text-b4l-text-muted">suport real vânzător</span>
                    </div>
                  </div>

                  <p className="mt-2 text-[11px] text-b4l-text-muted">
                    Tile-urile Vouchere și Total suport (albastru) includ și voucherele suportate de
                    eMAG (gri, giftcard-uri TVA 0). Verde = costul tău real de campanie.
                  </p>
                </div>
                <p className="mt-2 text-[11px] text-b4l-text-muted">
                  Suport = reduceri suportate de vânzător: vouchere de tip cod reducere (din
                  raw_json.vouchers) + reducerea de preț la listare (original_price − sale_price) ×
                  cantitate. Valorile principale sunt cu TVA, ca să se compare cu vânzările.
                </p>
              </Card>
            )
          })()}

          {/* Trend lunar suport campanie (voucher + reducere pret, cu TVA) */}
          {data.campaignTrend.length > 0 &&
            (() => {
              const tMax = Math.max(1, ...data.campaignTrend.map((t) => t.totalGross))
              const sumSeller = data.campaignTrend.reduce((a, t) => a + t.voucherSellerGross, 0)
              const sumEmag = data.campaignTrend.reduce((a, t) => a + t.voucherEmagGross, 0)
              const sumPricecut = data.campaignTrend.reduce((a, t) => a + t.pricecutGross, 0)
              const sumTotal = data.campaignTrend.reduce((a, t) => a + t.totalGross, 0)
              const seg = (v: number) => `${(v / tMax) * 100}%`
              return (
                <Card title="Trend lunar suport campanie (cu TVA)">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs sm:table-fixed">
                      <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                        <tr>
                          <th className="w-12 py-1.5 pr-2">Luna</th>
                          <th className="w-28 py-1.5 pr-2 text-right">
                            <span className="inline-flex items-center gap-1">
                              <span className="inline-block h-2 w-2 rounded-full bg-b4l-blue/70" />
                              Voucher seller
                            </span>
                          </th>
                          <th className="w-28 py-1.5 pr-2 text-right">
                            <span className="inline-flex items-center gap-1">
                              <span className="inline-block h-2 w-2 rounded-full bg-b4l-text-muted/50" />
                              Voucher eMAG
                            </span>
                          </th>
                          <th className="w-28 py-1.5 pr-2 text-right">
                            <span className="inline-flex items-center gap-1">
                              <span className="inline-block h-2 w-2 rounded-full bg-amber-500/70" />
                              Reducere preț
                            </span>
                          </th>
                          <th className="hidden py-1.5 pr-2 sm:table-cell">Distribuție</th>
                          <th className="w-24 py-1.5 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.campaignTrend.map((t) => (
                          <tr key={t.month} className="border-b border-b4l-border last:border-0">
                            <td className="py-1.5 pr-2 text-b4l-text-muted">{t.month}</td>
                            <td className="py-1.5 pr-2 text-right text-b4l-blue-dark">
                              {lei(t.voucherSellerGross)}
                            </td>
                            <td className="py-1.5 pr-2 text-right text-b4l-text-muted">
                              {lei(t.voucherEmagGross)}
                            </td>
                            <td className="py-1.5 pr-2 text-right text-amber-700">
                              {lei(t.pricecutGross)}
                            </td>
                            <td className="hidden py-1.5 pr-2 sm:table-cell">
                              <div className="flex h-3 w-full overflow-hidden rounded bg-b4l-bg-soft">
                                <div
                                  className="h-3 bg-b4l-blue/70"
                                  style={{ width: seg(t.voucherSellerGross) }}
                                  title={`Voucher seller: ${lei(t.voucherSellerGross)} lei`}
                                />
                                <div
                                  className="h-3 bg-b4l-text-muted/40"
                                  style={{ width: seg(t.voucherEmagGross) }}
                                  title={`Voucher eMAG: ${lei(t.voucherEmagGross)} lei`}
                                />
                                <div
                                  className="h-3 bg-amber-500/70"
                                  style={{ width: seg(t.pricecutGross) }}
                                  title={`Reducere preț: ${lei(t.pricecutGross)} lei`}
                                />
                              </div>
                            </td>
                            <td className="py-1.5 text-right font-semibold">{lei(t.totalGross)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-b4l-border font-semibold">
                          <td className="py-1.5 pr-2">Total perioadă</td>
                          <td className="py-1.5 pr-2 text-right text-b4l-blue-dark">
                            {lei(sumSeller)}
                          </td>
                          <td className="py-1.5 pr-2 text-right text-b4l-text-muted">
                            {lei(sumEmag)}
                          </td>
                          <td className="py-1.5 pr-2 text-right text-amber-700">
                            {lei(sumPricecut)}
                          </td>
                          <td className="hidden sm:table-cell" />
                          <td className="py-1.5 text-right">{lei(sumTotal)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <p className="mt-2 text-[11px] text-b4l-text-muted">
                    Voucher seller (albastru) = suportat de tine; Voucher eMAG (gri) = giftcard-uri
                    eMAG (nu te costă); Reducere preț (chihlimbar) = preț listă − preț vânzare.
                    Distribuția arată proporția celor trei, raportată la cea mai mare lună.
                  </p>
                </Card>
              )
            })()}

          {/* Tabele */}
          <div className="grid gap-3 lg:grid-cols-2">
            <Card title="Comenzi pe stare">
              <table className="w-full text-sm">
                <thead className="border-b border-b4l-border text-left text-xs text-b4l-text-muted">
                  <tr>
                    <th className="py-1.5 pr-3">Stare</th>
                    <th className="py-1.5 pr-3 text-right">Nr.</th>
                    <th className="py-1.5 pr-3 text-right">Valoare</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.byStatus.map((s) => {
                    // Anulatele si returnatele se arata cu valoarea lor, dar nu intra in total.
                    const inafara = s.status === 0 || s.status === 5
                    return (
                      <tr key={s.status} className="border-b border-b4l-border">
                        <td className="py-1.5 pr-3">
                          {s.label}
                          {inafara && (
                            <span className="ml-1 text-[11px] text-b4l-text-muted">
                              (nu intră în valoare)
                            </span>
                          )}
                        </td>
                        <td
                          className={`py-1.5 pr-3 text-right ${inafara ? 'text-b4l-text-muted' : ''}`}
                        >
                          {nr(s.count)}
                        </td>
                        <td
                          className={`py-1.5 pr-3 text-right ${inafara ? 'text-b4l-text-muted' : ''}`}
                        >
                          {lei(s.value)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-b4l-border font-semibold">
                    <td className="py-1.5 pr-3">Total vândut (fără anulate și returnate)</td>
                    <td className="py-1.5 pr-3 text-right">{nr(data.orders.valueCount)}</td>
                    <td className="py-1.5 pr-3 text-right">{lei(data.orders.value)}</td>
                  </tr>
                </tfoot>
              </table>
              <p className="mt-2 text-[11px] text-b4l-text-muted">
                Total valoare = KPI-ul Valoare comenzi de mai sus (cheie de verificare). O comandă
                returnată sau anulată nu intră deloc în valoare; un retur parțial rămâne încă la
                valoarea inițială a comenzii.
              </p>
            </Card>

            <Card title="Facturi Dante pe categorii (cu TVA)">
              {data.commissions.byCategory.length === 0 ? (
                <p className="text-sm text-b4l-text-muted">Fără facturi Dante în perioadă.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 border-b border-b4l-border bg-white text-left text-b4l-text-muted">
                      <tr>
                        <th className="py-1 pr-3">Categorie</th>
                        <th className="py-1 pr-3 text-right">Nr.</th>
                        <th className="py-1 pr-3 text-right">Valoare</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.commissions.byCategory.map((c) => (
                        <tr key={c.category} className="border-b border-b4l-border last:border-0">
                          <td className="py-1 pr-3">
                            {invoiceTypeLabel(c.category, c.categoryName)}{' '}
                            <span className="text-b4l-text-muted">({c.category})</span>
                          </td>
                          <td className="py-1 pr-3 text-right">{nr(c.count)}</td>
                          <td className="py-1 pr-3 text-right">{lei(c.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {/* Trend lunar + Top produse */}
          <div className="grid gap-3 lg:grid-cols-2">
            <Card title="Trend lunar">
              {data.trend.length === 0 ? (
                <p className="text-sm text-b4l-text-muted">Fără date în perioadă.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.trend.map((t) => (
                    <div key={t.month} className="flex items-center gap-2 text-xs">
                      <span className="w-14 shrink-0 text-b4l-text-muted">{t.month}</span>
                      <div className="h-4 flex-1 overflow-hidden rounded bg-b4l-bg-soft">
                        <div
                          className="h-4 rounded bg-b4l-blue/70"
                          style={{ width: `${(t.value / trendMax) * 100}%` }}
                        />
                      </div>
                      <span className="w-24 shrink-0 text-right">{lei(t.value)}</span>
                      <span className="w-10 shrink-0 text-right text-b4l-text-muted">
                        {nr(t.count)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {data.trend.length > 0 && (
                <div className="mt-4 border-t border-b4l-border pt-3">
                  <div className="mb-1 text-xs font-medium text-b4l-text-muted">
                    Evoluție lunară % (recurenți · card · ramburs · retururi)
                  </div>
                  <PctTrendChart data={data.trend} />
                </div>
              )}
            </Card>

            <Card title="Top produse vândute">
              {data.topProducts.length === 0 ? (
                <p className="text-sm text-b4l-text-muted">Fără produse în perioadă.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 border-b border-b4l-border bg-white text-left text-b4l-text-muted">
                      <tr>
                        <th className="py-1 pr-3">Produs</th>
                        <th className="py-1 pr-3 text-right">Cant.</th>
                        <th className="py-1 pr-3 text-right">Valoare</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topProducts.map((p, i) => (
                        <tr key={i} className="border-b border-b4l-border last:border-0">
                          <td className="py-1 pr-3">{p.name ?? '—'}</td>
                          <td className="py-1 pr-3 text-right">{nr(p.qty)}</td>
                          <td className="py-1 pr-3 text-right">{lei(p.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  )
}
